import React, { useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
} from "@mui/material";
import NotesInput from "./NotesInput";
import Alert from "../v3/Alert";
import Btn from "../v3/Btn";

const DEFAULT_DECISION_OPTIONS = [
  { value: "APPROVED", label: "Approve", tone: "ok" },
  { value: "REJECTED", label: "Reject", tone: "danger" },
  // Neither an approval nor a rejection: the reviewer is asking the investor
  // for more, so it carries the neutral tone rather than the danger one.
  { value: "NEEDS_INFO", label: "Request more information", tone: "neutral" },
];

const radioStyle = (tone, selected) => {
  const baseColor = {
    ok:      "var(--pos-500)",
    danger:  "var(--neg-500)",
    neutral: "var(--ink-500)",
  }[tone || "neutral"];
  const baseBg = {
    ok:      "var(--pos-100)",
    danger:  "var(--neg-100)",
    neutral: "var(--info-100)",
  }[tone || "neutral"];
  return {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "8px 14px",
    border: `1px solid ${selected ? baseColor : "var(--bd)"}`,
    background: selected ? baseBg : "var(--bg-card)",
    borderRadius: "var(--r-md)",
    cursor: "pointer",
    fontSize: "var(--t-md)",
    fontWeight: 500,
    color: selected ? baseColor : "var(--tx)",
    transition: "background 120ms, border-color 120ms",
  };
};

function DecisionForm({
  decisionOptions,
  // The radio group name and the textarea / dialog ids used to be hardcoded.
  // ComplianceDecisionDetail mounts two of these forms on one page, where a
  // shared radio `name` makes the two groups one group (picking Review clears
  // Reverse) and a shared id makes the label point at the wrong textarea.
  name = "dr-decision",
  idPrefix = "dr-decision",
  notesLabel = "Decision notes",
  submitLabel = "Submit decision",
  confirmTitle = "Confirm decision",
  confirmBody,
  minNotesLength = 10,
  onSubmit,
  disabled = false,
  busyLabel = "Saving...",
  initialDecision = null,
  requireDecision = true,
}) {
  const options = decisionOptions || DEFAULT_DECISION_OPTIONS;
  const [decision, setDecision] = useState(initialDecision);
  const [notes, setNotes] = useState("");
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  // A submit handler may refetch the page it lives on, which unmounts this
  // form mid-flight. Without the guard every 409 refetch logs a React warning
  // about updating an unmounted component.
  const alive = useRef(true);
  useEffect(() => () => { alive.current = false; }, []);

  const notesTrimmed = (notes || "").trim();
  const notesValid = notesTrimmed.length >= minNotesLength;
  const decisionValid = !requireDecision || !!decision;
  const canSubmit = notesValid && decisionValid && !disabled && !busy;

  async function handleConfirm() {
    setError(null);
    setBusy(true);
    try {
      await onSubmit({ decision, notes: notesTrimmed });
      if (!alive.current) return;
      setConfirming(false);
      setNotes("");
      setDecision(initialDecision);
    } catch (err) {
      if (!alive.current) return;
      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Submission failed. Please retry."
      );
    } finally {
      if (alive.current) setBusy(false);
    }
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      {requireDecision && (
        <fieldset
          disabled={disabled || busy}
          style={{ border: 0, padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: 8 }}
        >
          <legend
            style={{
              fontFamily: "var(--f-mono)",
              fontSize: "var(--t-xs)",
              textTransform: "uppercase",
              letterSpacing: "0.06em",
              color: "var(--tx-3)",
              padding: 0,
            }}
          >
            Decision
          </legend>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {options.map((opt) => {
              const selected = decision === opt.value;
              return (
                <label key={opt.value} style={radioStyle(opt.tone, selected)}>
                  <input
                    type="radio"
                    name={name}
                    value={opt.value}
                    checked={selected}
                    onChange={() => setDecision(opt.value)}
                    style={{ margin: 0 }}
                  />
                  <span>{opt.label}</span>
                </label>
              );
            })}
          </div>
        </fieldset>
      )}

      <NotesInput
        id={`${idPrefix}-notes`}
        label={notesLabel}
        value={notes}
        onChange={setNotes}
        minLength={minNotesLength}
        required
        disabled={disabled || busy}
      />

      {error && <Alert tone="danger">{error}</Alert>}

      <div>
        <Btn
          variant="primary"
          disabled={!canSubmit}
          onClick={() => setConfirming(true)}
        >
          {submitLabel}
        </Btn>
      </div>

      <Dialog
        open={confirming}
        onClose={() => !busy && setConfirming(false)}
        aria-labelledby={`${idPrefix}-confirm-title`}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle id={`${idPrefix}-confirm-title`}>{confirmTitle}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {confirmBody ||
              "This decision is written to the compliance audit log. A CCO or UDP can review or reverse it."}
          </DialogContentText>
          {requireDecision && decision && (
            <p style={{ marginTop: 12, fontSize: "var(--t-md)" }}>
              <strong>Decision:</strong>{" "}
              {options.find((o) => o.value === decision)?.label || decision}
            </p>
          )}
        </DialogContent>
        <DialogActions>
          <Btn onClick={() => setConfirming(false)} disabled={busy}>
            Back
          </Btn>
          <Btn variant="primary" onClick={handleConfirm} disabled={busy} autoFocus>
            {busy ? busyLabel : "Confirm & submit"}
          </Btn>
        </DialogActions>
      </Dialog>
    </div>
  );
}

export default DecisionForm;
